import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  Grow,
  Fade,
  Chip,
  Stack,
  Divider,
  LinearProgress,
  Paper,
} from '@mui/material';
import { styled, keyframes, alpha, useTheme } from '@mui/material/styles';
import ClockLoader from '../common/ClockLoader';
import { useAuth } from '../../contexts/AuthContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SyncIcon from '@mui/icons-material/Sync';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import RealTimeIcon from '@mui/icons-material/Bolt';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

/* ===================== CONSTANT ===================== */
const SQUARE_CLIENT_ID = 'sq0idp-j4o6i9Ynono1SFrdSFgxqg';

/* ===================== ANIMATIONS ===================== */
const floatAnimation = keyframes`
  0%,100%{ transform: translateY(0); }
  50%{ transform: translateY(-6px); }
`;

const pulseAnimation = keyframes`
  0% { opacity: 1; }
  50% { opacity: 0.6; }
  100% { opacity: 1; }
`;

const syncAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  padding: theme.spacing(1),
  animation: `${fadeInUp} 0.6s ease-out`,
  boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.08)}`,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.light, 0.03)} 100%)`,
  overflow: 'hidden',
  position: 'relative',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
  },
}));

const Floating = styled('div')(() => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${floatAnimation} 3s ease-in-out infinite`,
}));

const SyncIconAnimated = styled(SyncIcon)(() => ({
  animation: `${syncAnimation} 2s linear infinite`,
}));

const LiveSyncBadge = styled(Chip)(({ theme }) => ({
  animation: `${pulseAnimation} 2s ease-in-out infinite`,
  fontWeight: 700,
  background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
  color: 'white',
}));

/* ===================== COMPONENT ===================== */
const POSIntegration = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentSiteId } = useAuth();

  const [step, setStep] = useState(1);
  const [system, setSystem] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');

  const [openSnack, setOpenSnack] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.toString()) {
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, [navigate]);

  // When we land on this screen and have a current site, try to load any
  // available Square locations using the stored integration token.
  useEffect(() => {
    const loadLocations = async () => {
      if (!currentSiteId) return;
      try {
        const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
        const res = await fetch(`${baseUrl}/api/pos/sales/square/${currentSiteId}/locations`);
        if (!res.ok) return;
        const body = await res.json();
        if (body && Array.isArray(body.locations)) {
          setLocations(body.locations);
        }
      } catch (e) {
        // Silent fail – user can still connect Square from onboarding.
      }
    };
    loadLocations();
  }, [currentSiteId]);

  const handleNext = () => {
    if (!system) return;
    if (system === 'square') {
      const state = Math.random().toString(36).substring(2);
      // Redirect URI must match what's configured in Square Developer Portal
      // Default: /dashboard/pos/callback (where our callback handler is located)
      const redirectUri =
        process.env.REACT_APP_SQUARE_REDIRECT_URI ||
        `${window.location.origin}/dashboard/pos/callback`;
      const scopes = encodeURIComponent(
        'PAYMENTS_READ PAYMENTS_WRITE MERCHANT_PROFILE_READ'
      );
      const url =
        `https://connect.squareup.com/oauth2/authorize` +
        `?client_id=${encodeURIComponent(SQUARE_CLIENT_ID)}` +
        `&scope=${scopes}` +
        `&session=false` +
        `&state=${state}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}`;
      window.location.assign(url);
      return;
    }
    setStep(2);
  };

  const handleSaveLocation = async () => {
    if (!currentSiteId || !selectedLocationId) return;
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5008';
      const res = await fetch(
        `${baseUrl}/api/pos/sales/square/${currentSiteId}/location`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ location_id: selectedLocationId }),
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setSnackMessage(body.message || 'Failed to save POS location');
        setSnackSeverity('error');
        setOpenSnack(true);
        return;
      }
      setSnackMessage('POS location saved for this site. Sales sync will now use this location.');
      setSnackSeverity('success');
      setOpenSnack(true);
    } catch (e) {
      setSnackMessage('Failed to save POS location');
      setSnackSeverity('error');
      setOpenSnack(true);
    }
  };

  const handleConnect = () => {
    setConnecting(true);
    setTimeout(() => {
      setConnecting(false);
      setSnackMessage('POS connected successfully! Live sync activated.');
      setSnackSeverity('success');
      setOpenSnack(true);
      setTimeout(() => navigate('/dashboard'), 1200);
    }, 2500);
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, p: 2 }}>
      <Grow in timeout={500}>
        <Box sx={{ width: '100%', maxWidth: 900 }}>
          <AnimatedCard>
            <CardContent sx={{ p: 4 }}>
              {/* Header Section */}
              <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Floating>
                    <CloudSyncIcon 
                      sx={{ 
                        fontSize: 48, 
                        color: theme.palette.primary.main,
                        filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.3)})`
                      }} 
                    />
                  </Floating>
                  <Box flex={1}>
                    <Typography variant="h4" fontWeight={800} gutterBottom>
                      Connect Your POS System
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                      Sync your point-of-sale data in real-time for seamless inventory and sales management.
                    </Typography>
                    <LiveSyncBadge 
                      icon={<RealTimeIcon />} 
                      label="LIVE SYNC ACTIVE" 
                      size="small"
                    />
                  </Box>
                </Stack>
              </Box>

              {/* Features Grid */}
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 3, 
                  mb: 4, 
                  borderRadius: 3,
                  background: alpha(theme.palette.primary.main, 0.03),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} divider={<Divider orientation="vertical" flexItem />}>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <SyncIconAnimated sx={{ color: theme.palette.success.main, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Real-time Updates</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Instant sync of sales, inventory, and orders
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <SecurityIcon sx={{ color: theme.palette.info.main, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Secure Connection</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Bank-level encryption for your data
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center' }}>
                    <CloudSyncIcon sx={{ color: theme.palette.warning.main, mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600}>Cloud Backup</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatic backup of all transactions
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              {/* Main Form Area */}
              <Box sx={{ mt: 4 }}>
                {step === 1 && (
                  <Fade in>
                    <Box sx={{ display: 'grid', gap: 3 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Select Your POS Provider
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Choose from our supported systems. More providers coming soon.
                        </Typography>
                      </Box>
                      
                      <FormControl fullWidth size="medium">
                        <InputLabel>POS System</InputLabel>
                        <Select
                          value={system}
                          label="POS System"
                          onChange={(e) => setSystem(e.target.value)}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 2,
                            }
                          }}
                        >
                          <MenuItem value="lightspeed">🚀 LightSpeed Retail</MenuItem>
                          <MenuItem value="square">💳 Square POS</MenuItem>
                          
                        </Select>
                      </FormControl>

                      {system && (
                        <Fade in timeout={300}>
                          <Paper 
                            elevation={0}
                            sx={{ 
                              p: 2.5, 
                              borderRadius: 2,
                              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                              background: alpha(theme.palette.success.main, 0.05),
                            }}
                          >
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <CheckCircleIcon color="success" />
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {system === 'square' 
                                    ? 'You will be redirected to Square to authorize the connection.'
                                    : 'Enter your credentials to establish a secure connection.'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {system === 'square' 
                                    ? 'We use OAuth 2.0 for secure, password-less authentication.'
                                    : 'Your credentials are encrypted and never stored.'}
                                </Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        </Fade>
                      )}
                    </Box>
                  </Fade>
                )}

                {step === 2 && (
                  <Fade in>
                    <Box sx={{ display: 'grid', gap: 3 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={600} gutterBottom>
                          Connect to LightSpeed
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Enter your LightSpeed credentials to enable real-time sync
                        </Typography>
                      </Box>

                      <TextField
                        label="Store ID / Email"
                        fullWidth
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        size="medium"
                        InputProps={{
                          sx: { borderRadius: 2 }
                        }}
                      />
                      <TextField
                        label="API Key / Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        size="medium"
                        InputProps={{
                          sx: { borderRadius: 2 }
                        }}
                      />

                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          borderRadius: 2,
                          border: `1px dashed ${alpha(theme.palette.divider, 0.5)}`,
                          background: alpha(theme.palette.info.main, 0.02),
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          🔒 Your credentials are encrypted using AES-256 encryption and transmitted over SSL.
                          We never store your actual passwords.
                        </Typography>
                      </Paper>
                    </Box>
                  </Fade>
                )}

                {/* Square location selector for the currently-selected RTN site */}
                {locations.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      Map Square Location to This Site
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select which Square location should be treated as this RTN site. We&apos;ll
                      only sync sales from that location.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                      <FormControl fullWidth size="medium" sx={{ maxWidth: 360 }}>
                        <InputLabel>Square Location</InputLabel>
                        <Select
                          value={selectedLocationId}
                          label="Square Location"
                          onChange={(e) => setSelectedLocationId(e.target.value)}
                          sx={{
                            '& .MuiSelect-select': {
                              py: 1.5,
                            },
                          }}
                        >
                          {locations.map((loc) => (
                            <MenuItem key={loc.id} value={loc.id}>
                              {loc.name || loc.id}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Button
                        variant="contained"
                        onClick={handleSaveLocation}
                        disabled={!selectedLocationId}
                        sx={{ whiteSpace: 'nowrap', borderRadius: 3, px: 3, py: 1.2 }}
                      >
                        Save Location
                      </Button>
                    </Stack>
                  </Box>
                )}
              </Box>

              {/* Sync Status (Optional) */}
              {connecting && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Establishing live connection...
                  </Typography>
                  <LinearProgress 
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      background: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        borderRadius: 4,
                      }
                    }} 
                  />
                </Box>
              )}
            </CardContent>

            {/* Actions */}
            <CardActions sx={{ p: 3, pt: 2, justifyContent: 'space-between', borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Typography variant="caption" color="text.secondary">
                ⚡ Powered by secure API technology
              </Typography>
              
              <Stack direction="row" spacing={2}>
                {step === 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={!system}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      px: 4,
                      py: 1.2,
                      borderRadius: 3,
                      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      '&:hover': {
                        background: `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                      }
                    }}
                  >
                    Continue
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={() => setStep(1)} 
                      variant="outlined"
                      sx={{ borderRadius: 3 }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleConnect}
                      disabled={!identifier || !password}
                      startIcon={<CloudSyncIcon />}
                      sx={{
                        px: 4,
                        py: 1.2,
                        borderRadius: 3,
                        background: `linear-gradient(90deg, ${theme.palette.success.main}, ${theme.palette.info.main})`,
                        '&:hover': {
                          background: `linear-gradient(90deg, ${theme.palette.success.dark}, ${theme.palette.info.dark})`,
                        }
                      }}
                    >
                      Connect & Sync
                    </Button>
                  </>
                )}
              </Stack>
            </CardActions>
          </AnimatedCard>
        </Box>
      </Grow>

      {/* Loading Overlay */}
      {connecting && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(4px)',
          }}
        >
          <ClockLoader size={100} />
          <Typography variant="h6" sx={{ mt: 3, fontWeight: 600 }}>
            Activating Live Sync...
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Connecting to your POS system in real-time
          </Typography>
        </Box>
      )}

      {/* Success Notification */}
      <Snackbar
        open={openSnack}
        autoHideDuration={4000}
        onClose={() => setOpenSnack(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity={snackSeverity}
          variant="filled"
          icon={<SyncIcon sx={{ animation: `${syncAnimation} 2s linear infinite` }} />}
          sx={{
            borderRadius: 3,
            boxShadow: `0 8px 24px ${alpha(theme.palette[snackSeverity].main, 0.3)}`,
          }}
        >
          <Typography fontWeight={700}>{snackMessage}</Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSIntegration;